/**
 * PermessiPage — Console operatore /permessi (PERMESSI-EDILIZI-MODEL §10, piano 5 passo 5.8): come sta la raccolta dei
 * permessi di costruire dagli albi dei comuni, in SOLA LETTURA.
 *
 * In cima i numeri che dicono se tutto gira (comuni letti, pratiche del mese, documenti in coda, sblocchi del mese);
 * sotto, i documenti che il sistema non è riuscito a leggere, per motivo (dove guardare quando una pratica manca); in
 * fondo ogni comune col suo albo, PRIMA quelli che hanno un problema. Costi per documento non ce ne sono: i documenti
 * si leggono come testo, senza modelli AI (le scansioni restano non lette, col loro motivo).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Link, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { creaContestoLocale } from '@gestionale/core/i18n/lingue';
import { formatData, formatNumero } from '@gestionale/core/i18n/formattazione';
import type { MotivoNonLetta } from '@shared/permessi/albi';
import type { IdCategoriaLavoro } from '@shared/permessi/categorieLavoro';
import { tokens } from '../theme';
import { SoftChip, type SoftChipTone } from '../components/SoftChip';
import { MOTIVI_NON_LETTA, albiInOrdine, leggiQuadroPermessi, type AlboDelComune, type QuadroPermessi } from '../services/permessiConsoleService';
import { archivioDelComune } from '../services/permessiConsoleRegole';

/** Cosa vuol dire ogni motivo, detto a chi deve decidere se intervenire. */
/** I lavori come li legge chi guarda la console (piano 8). La console è solo in italiano. */
const CATEGORIE_LAVORO_CONSOLE: Record<IdCategoriaLavoro, string> = {
  nuova_costruzione: 'edifici nuovi',
  ristrutturazione: 'ristrutturazioni',
  infissi: 'infissi',
  coibentazione: 'cappotto e isolamento',
  tetto: 'tetto',
  fotovoltaico: 'fotovoltaico',
  clima_acqua_calda: 'clima e acqua calda',
  ricarica_veicoli: 'ricarica delle auto',
  esterni: 'cancelli, recinzioni e pergole',
  non_edifici: 'non edifici',
};

const MOTIVI: Record<MotivoNonLetta, string> = {
  documento_assente: 'Nessun documento allegato all’avviso',
  troppo_grande: 'Documento troppo grande da scaricare',
  scansione: 'Documento scansionato (immagine, niente testo)',
  piattaforma_sconosciuta: 'Documento di una piattaforma che non sappiamo leggere',
  formato_non_riconosciuto: 'PDF leggibile ma non è un atto della piattaforma regionale',
  chiave_mancante: 'Atto senza codice né numero: non si sa a che pratica legarlo',
  documento_illeggibile: 'PDF rovinato o non scaricabile',
  non_a_cura_di_privati: 'Lavoro pagato dal comune o da un ente pubblico: lo vede il Radar delle gare',
};

const STATI_FONTE: Record<string, string> = {
  pronta: 'Albo trovato',
  sito_mancante: 'Sito del comune mancante',
  albo_non_trovato: 'Albo non trovato',
  fornitore_senza_adattatore: 'Fornitore che non sappiamo leggere',
  errore: 'Errore nella scoperta',
  respinta: 'L’albo ci respinge (si riprova fra un mese)',
};

function salute(a: AlboDelComune): { testo: string; tono: SoftChipTone } {
  if (a.fonte.stato !== 'pronta') return { testo: STATI_FONTE[a.fonte.stato] || a.fonte.stato, tono: 'error' };
  if (!a.lettore) return { testo: 'Mai letto', tono: 'warning' };
  if (a.lettore.salute === 'ferma') return { testo: 'Fermo', tono: 'error' };
  if (a.lettore.salute === 'degradata') return { testo: 'A singhiozzo', tono: 'warning' };
  return { testo: 'Funziona', tono: 'success' };
}

const STATI_ARCHIVIO = { funziona: null, a_singhiozzo: { testo: 'A singhiozzo', tono: 'warning' }, fermo: { testo: 'Fermo', tono: 'error' } } as const;

/** Gli anni dell'archivio già letti e, se c'è un problema, il suo stato; «—» se il comune non ha un archivio che leggiamo. */
const Archivio: React.FC<{ albo: AlboDelComune }> = ({ albo }) => {
  const a = archivioDelComune(albo);
  if (!a) return <>—</>;
  const problema = STATI_ARCHIVIO[a.stato];
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <span>{a.anni.length ? a.anni.join(', ') : 'Nessun anno letto'}{a.in_parte ? ' (l’ultimo in parte)' : ''}</span>
      {problema && <SoftChip tone={problema.tono} label={problema.testo} />}
    </Stack>
  );
};

const Numero: React.FC<{ valore: string; etichetta: string }> = ({ valore, etichetta }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 160 }}>
    <Typography sx={{ fontFamily: tokens.font.mono, fontVariantNumeric: 'tabular-nums', fontSize: '1.6rem', fontWeight: 600, color: tokens.color.graphite }}>{valore}</Typography>
    <Typography variant="body2" color="text.secondary">
      {etichetta}
    </Typography>
  </Paper>
);

export const PermessiPage: React.FC = () => {
  const { i18n } = useTranslation('menu');
  const ctx = useMemo(() => creaContestoLocale({ lingua: i18n.language, paese: 'IT', valuta: 'EUR', fusoOrario: 'Europe/Rome' }), [i18n.language]);
  const [quadro, setQuadro] = useState<QuadroPermessi | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [caricamento, setCaricamento] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      setQuadro(await leggiQuadroPermessi());
    } catch (e) {
      console.error('[console/permessi] lettura fallita', e);
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setCaricamento(false);
    }
  }, []);
  useEffect(() => void carica(), [carica]);

  const albi = useMemo(() => (quadro ? albiInOrdine(quadro.albi) : []), [quadro]);
  const funzionanti = albi.filter((a) => salute(a).tono === 'success').length;
  const n = (x: number) => formatNumero(x, ctx);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: tokens.color.graphite }}>
            Permessi edilizi
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            La raccolta dei permessi di costruire dagli albi dei comuni: cosa funziona, cosa no e perché. Gli albi si leggono alle 3 e alle 14.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => void carica()} disabled={caricamento} sx={{ minHeight: 40 }}>
          Aggiorna
        </Button>
      </Stack>

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}
      {!quadro ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={90} />
          <Skeleton variant="rounded" height={260} />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Numero valore={`${n(funzionanti)} / ${n(albi.length)}`} etichetta="Albi che funzionano" />
            <Numero valore={n(quadro.pratiche.del_mese)} etichetta="Cantieri arrivati questo mese" />
            <Numero valore={n(quadro.pratiche.correnti)} etichetta="Cantieri in tutto" />
            <Numero valore={n(quadro.pratiche.storico)} etichetta="Pratiche lette dall'archivio" />
            <Numero valore={`${n(quadro.pratiche.classificate)} / ${n(quadro.pratiche.correnti + quadro.pratiche.storico)}`} etichetta="Pratiche classificate per tipo di lavoro" />
            <Numero valore={n(quadro.pubblicazioni.da_leggere)} etichetta="Documenti in coda da leggere" />
            <Numero valore={n(quadro.sblocchi_del_mese)} etichetta="Progettisti sbloccati questo mese" />
          </Stack>

          <Paper variant="outlined" sx={{ overflow: 'auto' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, px: 2, pt: 1.5 }}>
              {`Le aziende col modulo (${n(quadro.aziende.length)}) e i prodotti per cui lo usano`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
              Ogni azienda vede solo i cantieri dei suoi prodotti. Senza prodotti vede soltanto le pratiche da verificare: si impostano con lo script prodotti-del-modulo.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Azienda</TableCell>
                  <TableCell>Prodotti</TableCell>
                  <TableCell>Cantieri che vede (oltre a case nuove e ristrutturazioni)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quadro.aziende.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.nome}</TableCell>
                    <TableCell sx={{ color: a.prodotti ? undefined : tokens.color.danger }}>{a.prodotti ? a.prodotti.join(', ') || 'nessuno' : 'Non impostati'}</TableCell>
                    <TableCell>{a.categorie.map((c) => CATEGORIE_LAVORO_CONSOLE[c]).join(', ') || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, px: 2, pt: 1.5 }}>
              Documenti non letti, per motivo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
              {`Letti ${n(quadro.pubblicazioni.lette)}. Un documento non letto non diventa una pratica: se un cantiere manca, il motivo è qui.`}
            </Typography>
            <Table size="small">
              <TableBody>
                {MOTIVI_NON_LETTA.map((m) => (
                  <TableRow key={m}>
                    <TableCell>{MOTIVI[m]}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.font.mono, width: 120 }}>
                      {n(quadro.pubblicazioni.non_lette[m])}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'auto' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, px: 2, pt: 1.5, pb: 1 }}>
              {`Gli albi dei comuni (${n(albi.length)}), prima quelli con un problema`}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Comune</TableCell>
                  <TableCell>Fornitore</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Ultima lettura</TableCell>
                  <TableCell>Archivio</TableCell>
                  <TableCell>Ultimo errore</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {albi.map((a) => {
                  const s = salute(a);
                  const indirizzo = a.fonte.url_albo_manuale || a.fonte.url_albo;
                  return (
                    <TableRow key={a.fonte.cf}>
                      <TableCell>
                        {indirizzo ? (
                          <Link href={indirizzo} target="_blank" rel="noopener noreferrer">
                            {a.fonte.comune}
                          </Link>
                        ) : (
                          a.fonte.comune
                        )}
                        {a.fonte.provincia ? ` (${a.fonte.provincia})` : ''}
                      </TableCell>
                      <TableCell>{a.fonte.fornitore || '—'}</TableCell>
                      <TableCell>
                        <SoftChip tone={s.tono} label={s.testo} />
                      </TableCell>
                      <TableCell>{a.lettore && a.lettore.ultima_esecuzione_il ? formatData(a.lettore.ultima_esecuzione_il, ctx, 'dataOra') : '—'}</TableCell>
                      <TableCell>
                        <Archivio albo={a} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420, color: 'text.secondary' }}>
                        {(a.lettore && a.lettore.errori_recenti[0]) || a.fonte.motivo || (a.archivio && a.archivio.salute !== 'buona' && a.archivio.errori_recenti[0]) || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default PermessiPage;
